import { z } from 'zod';
import { baseComponentSchema } from '../component-base.schemas';

// Navbar link schema
export const navbarLinkSchema = z.object({
  text: z.string().min(1, 'Link text is required'),
  url: z.string().min(1, 'Link URL is required'),
  order: z.number().int().min(0),
  style: z.enum(['normal', 'button', 'outline']).default('normal'),
  target: z.enum(['_self', '_blank']).optional(),
});

// Navbar logo schema — text or image only (legacy "both" still accepted when reading old data)
export const navbarLogoSchema = z
  .object({
    mode: z.enum(['text', 'image', 'both']).default('text'),

    // Text-based logo
    text: z.string().min(1).max(100).optional(),
    textSize: z.enum(['small', 'medium', 'large', 'xl']).default('medium').optional(),
    textWeight: z.enum(['normal', 'bold', 'light']).default('bold').optional(),
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .default('#000000')
      .optional(),

    // Image-based logo
    imageUrl: z.string().url().optional(),
    imageAlt: z.string().max(200).optional(),
    imageWidth: z.number().int().positive().max(320).default(120).optional(),
    imageHeight: z.number().int().positive().min(24).max(96).default(48).optional(),

    // Aspect ratio lock (default: true)
    lockAspectRatio: z.boolean().default(true).optional(),

    // Original image dimensions (captured on upload for aspect ratio calculations)
    originalWidth: z.number().int().positive().optional(),
    originalHeight: z.number().int().positive().optional(),

    // Image upload metadata
    mediaId: z.string().uuid().optional(),
    s3Key: z.string().optional(),
    cloudfrontUrl: z.string().url().optional(),

    // Deprecated combined-mode fields (legacy data only)
    imagePosition: z.enum(['left', 'right', 'top', 'bottom']).optional(),
    spacing: z.enum(['none', 'small', 'medium', 'large']).optional(),

    // Link settings
    linkUrl: z.string().url().optional(),
    linkTarget: z.enum(['_blank', '_self']).default('_self').optional(),
  })
  .refine(
    data => {
      // Validation rules based on mode
      if (data.mode === 'text' && !data.text) {
        return false;
      }
      if (data.mode === 'image' && !data.imageUrl && !data.mediaId) {
        return false;
      }
      if (data.mode === 'both' && (!data.text || (!data.imageUrl && !data.mediaId))) {
        return false;
      }
      return true;
    },
    {
      message: 'Logo configuration must match the selected mode',
    }
  );

// Navbar custom colors schema - allows overriding theme colors per component instance
export const navbarCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    brandText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    linkText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    linkHover: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const navbarDataSchema = z
  .object({
    logo: navbarLogoSchema.optional(),
    siteName: z.string().min(1, 'Site name is required').optional(), // Made optional since logo can replace it
    links: z.array(navbarLinkSchema),
    alignment: z.enum(['left', 'center', 'right', 'space-between']).default('space-between'),
    sticky: z.boolean().default(true),
    // DEPRECATED: Use customColors.background instead (kept for backward compatibility)
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    // DEPRECATED: Use customColors.text instead (kept for backward compatibility)
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    height: z.enum(['compact', 'normal', 'tall']).default('normal').optional(),
    shadow: z.boolean().default(true).optional(),
    borderBottom: z.boolean().default(true).optional(),
    // NEW: Component-specific custom colors that override theme colors
    customColors: navbarCustomColorsSchema,
  })
  .refine(
    data => {
      // Either siteName or logo must be provided
      return data.siteName || data.logo;
    },
    {
      message: 'Either site name or logo must be provided',
    }
  );

export const navbarComponentSchema = baseComponentSchema.extend({
  type: z.literal('navbar'),
  data: navbarDataSchema,
});

// Type exports
export type NavbarLink = z.infer<typeof navbarLinkSchema>;
export type NavbarLogo = z.infer<typeof navbarLogoSchema>;
export type NavbarData = z.infer<typeof navbarDataSchema>;
export type NavbarComponent = z.infer<typeof navbarComponentSchema>;
